import Session from "supertokens-auth-react/recipe/session";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:3000";

export interface SignInLinkResponse {
  status: string;
  createdNewRecipeUser: boolean;
  user: {
    id: string;
    isPrimaryUser: boolean;
    tenantIds: string[];
    email: string;
    phoneNumbers: string[];
    thirdParty: unknown[];
    webauthn: {
      credentialIds: string[];
    };
    loginMethods: Array<{
      recipeId: string;
      recipeUserId: string;
      tenantIds: string[];
      email: string;
      timeJoined: number;
      verified: boolean;
    }>;
    timeJoined: number;
  };
  albumId: string;
  ownerId: string;
}

class AuthService {
  private readonly ANONYMOUS_TOKEN_KEY = "st-anonymous-token";
  private readonly ANONYMOUS_USER_KEY = "st-anonymous-user";
  private readonly ANONYMOUS_LINK_DATA_KEY = "st-anonymous-link-data";
  private readonly ANONYMOUS_SHARE_TOKENS_KEY = "st-anonymous-share-tokens";

  async signInWithLink(token: string): Promise<SignInLinkResponse> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/signin/link/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ token }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      // Get the token from the header
      const accessToken = response.headers.get("st-access-token");
      if (!accessToken) {
        throw new Error("No access token received from server");
      }

      // Parse the response
      const data: SignInLinkResponse = await response.json();

      // Store the token, user data, and link data in localStorage
      this.setAnonymousToken(accessToken);
      this.setAnonymousUser(data.user);

      // Store the share token
      this.addAnonymousShareToken(token);

      return data;
    } catch (error) {
      console.error("Link signin failed:", error);
      throw error;
    }
  }

  setAnonymousToken(token: string): void {
    localStorage.setItem(this.ANONYMOUS_TOKEN_KEY, token);
  }

  getAnonymousToken(): string | null {
    return localStorage.getItem(this.ANONYMOUS_TOKEN_KEY);
  }

  setAnonymousUser(user: SignInLinkResponse["user"]): void {
    localStorage.setItem(this.ANONYMOUS_USER_KEY, JSON.stringify(user));
  }

  getAnonymousUser(): SignInLinkResponse["user"] | null {
    const userData = localStorage.getItem(this.ANONYMOUS_USER_KEY);
    if (!userData) return null;
    try {
      return JSON.parse(userData);
    } catch {
      return null;
    }
  }

  setAnonymousLinkData(linkData: { albumId: string; ownerId: string }): void {
    localStorage.setItem(this.ANONYMOUS_LINK_DATA_KEY, JSON.stringify(linkData));
  }

  getAnonymousLinkData(): { albumId: string; ownerId: string } | null {
    const linkData = localStorage.getItem(this.ANONYMOUS_LINK_DATA_KEY);
    if (!linkData) return null;
    try {
      return JSON.parse(linkData);
    } catch {
      return null;
    }
  }

  addAnonymousShareToken(token: string): void {
    const tokens = this.getAnonymousShareTokens();
    if (!tokens.includes(token)) {
      tokens.push(token);
      localStorage.setItem(this.ANONYMOUS_SHARE_TOKENS_KEY, JSON.stringify(tokens));
    }
  }

  getAnonymousShareTokens(): string[] {
    const tokensStr = localStorage.getItem(this.ANONYMOUS_SHARE_TOKENS_KEY);
    if (!tokensStr) return [];
    try {
      return JSON.parse(tokensStr);
    } catch {
      return [];
    }
  }

  removeAnonymousShareToken(token: string): void {
    const tokens = this.getAnonymousShareTokens().filter((t) => t !== token);
    localStorage.setItem(this.ANONYMOUS_SHARE_TOKENS_KEY, JSON.stringify(tokens));
  }

  clearAnonymousAuth(): void {
    localStorage.removeItem(this.ANONYMOUS_TOKEN_KEY);
    localStorage.removeItem(this.ANONYMOUS_USER_KEY);
    localStorage.removeItem(this.ANONYMOUS_LINK_DATA_KEY);
    localStorage.removeItem(this.ANONYMOUS_SHARE_TOKENS_KEY);
  }

  clearAnonymousAuthKeepTokens(): void {
    // Clear anonymous session but keep share tokens for merging after login
    localStorage.removeItem(this.ANONYMOUS_TOKEN_KEY);
    localStorage.removeItem(this.ANONYMOUS_USER_KEY);
    localStorage.removeItem(this.ANONYMOUS_LINK_DATA_KEY);
    // Intentionally NOT removing ANONYMOUS_SHARE_TOKENS_KEY
  }

  isAnonymousUser(): boolean {
    return !!this.getAnonymousToken();
  }

  async isLoggedInUser(): Promise<boolean> {
    return await Session.doesSessionExist();
  }

  async checkAuthentication(): Promise<void> {
    const hasSession = await Session.doesSessionExist();
    const hasAnonymousToken = this.isAnonymousUser();

    if (!hasSession && !hasAnonymousToken) {
      throw new Error("User not authenticated");
    }
  }

  async handleBackFromSharedAlbum(): Promise<void> {
    // Both anonymous and logged-in users should stay authenticated
    // This method is kept for backward compatibility but doesn't clear auth anymore
  }

  async mergeAnonymousShares(): Promise<void> {
    const tokens = this.getAnonymousShareTokens();

    if (tokens.length === 0) {
      return;
    }

    try {
      // Get the user ID from the session
      const sessionInfo = await Session.getAccessTokenPayloadSecurely();
      const userId = sessionInfo.id;

      if (!userId) {
        throw new Error("Failed to get user ID from session");
      }

      // Get access token from SuperTokens
      const accessToken = await Session.getAccessToken();
      if (!accessToken) {
        throw new Error("Failed to get access token");
      }

      const url = `${API_BASE_URL}/api/v1/users/${encodeURIComponent(userId)}/shares/merge`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${accessToken}`,
        },
        credentials: "include",
        body: JSON.stringify({ tokens }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to merge anonymous shares: ${errorText}`);
      }

      // SuperTokens automatically handles token refresh via cookies/headers
      // Force a token refresh to get the updated permissions immediately
      await Session.attemptRefreshingSession();

      // Clear the anonymous data after successful merge
      this.clearAnonymousAuth();
    } catch (error) {
      console.error("Error merging anonymous shares:", error);
      throw error;
    }
  }
}

export const authService = new AuthService();
